<template>
  <div class="sales-dashboard">
    <div class="sales-dashboard__topbar">
      <div>
        <div class="text-sm text-secondary">Sales executive dashboard</div>
        <h1 class="mt-1 text-3xl font-semibold md:text-5xl">{{ greeting }}, {{ salesExecutiveFirstName }}</h1>
      </div>
      <div class="sales-dashboard__period">Last 30 days</div>
    </div>

    <div class="sales-dashboard__grid">
      <Card
        class="sales-dashboard__profile"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="sales-profile-card"
      >
        <div>
          <div class="text-sm uppercase tracking-[0.16em] text-white/60">Personal performance</div>
          <div class="mt-2 text-3xl font-semibold">{{ salesExecutiveName }}</div>
          <div class="mt-3 text-sm text-white/70">{{ performanceSummary }}</div>
        </div>

        <div class="sales-profile-card__footer">
          <div>
            <div class="text-4xl font-semibold">{{ formatPercent(dashboardData.currentConversionRate) }}</div>
            <div class="text-sm text-white/70">Conversion</div>
          </div>
          <Avatar
            icon="handshake"
            color="success"
            size="lg"
            no-border
          />
        </div>
      </Card>

      <Card
        class="sales-dashboard__appointments"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="sales-card"
      >
        <div class="sales-card__header">
          <div>
            <div class="text-lg font-semibold">Today's appointments</div>
            <div class="text-sm text-secondary">{{ appointmentSubtitle }}</div>
          </div>
          <div class="sales-count-pill">{{ dashboardData.todaysAppointments.length }}</div>
        </div>

        <div
          v-if="dashboardData.todaysAppointments.length > 0"
          class="sales-appointment-list"
        >
          <RouterLink
            v-for="app in dashboardData.todaysAppointments"
            :key="app.id"
            :to="'/agreement/' + app.agreementId + '/appointments/' + app.id"
            class="sales-appointment-row"
          >
            <Avatar
              size="md"
              :icon="getDictionaryItem('appointmentMethod', app.method).icon"
              rounding="rounded"
              color="secondary"
              no-border
            />
            <div class="min-w-0 flex-1">
              <div class="truncate font-semibold">
                {{ (app.forename + ' ' + app.surname + ' ' + app.company).trim() }}
              </div>
              <div class="text-sm text-secondary">{{ getDictionaryItem('appointmentMethod', app.method).label }}</div>
            </div>
            <div class="sales-time-pill">{{ dayjs(app.time).format('h:ma') }}</div>
          </RouterLink>
        </div>

        <div
          v-else
          class="sales-empty-state"
        >
          <Avatar
            icon="calendar-check"
            color="success"
            size="lg"
            no-border
            class="mx-auto mb-3"
          />
          <div class="font-semibold">No appointments today</div>
          <div class="mt-1 text-sm text-secondary">A clear diary for the day.</div>
        </div>
      </Card>

      <Card
        class="sales-dashboard__metrics"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="sales-card"
      >
        <div class="sales-card__header">
          <div>
            <div class="text-lg font-semibold">30-day motion</div>
            <div class="text-sm text-secondary">Appointments, renewals, and value</div>
          </div>
        </div>

        <div class="sales-metric-strip">
          <div
            v-for="metric in performanceMetrics"
            :key="metric.label"
            class="sales-metric-tile"
          >
            <Avatar
              :icon="metric.icon"
              :color="metric.color"
              size="md"
              no-border
            />
            <div class="mt-4 text-2xl font-semibold">{{ metric.value }}</div>
            <div class="mt-1 text-sm text-secondary">{{ metric.label }}</div>
          </div>
        </div>
      </Card>

      <Card
        class="sales-dashboard__conversion"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="sales-card sales-card--conversion"
      >
        <div class="sales-card__header">
          <div>
            <div class="text-lg font-semibold">Conversion</div>
            <div class="text-sm text-secondary">Renewed from appointments</div>
          </div>
          <div class="sales-rate-pill">{{ formatPercent(dashboardData.currentConversionRate) }}</div>
        </div>
        <div class="sales-conversion-visual">
          <div class="sales-conversion-visual__rate">{{ formatPercent(dashboardData.currentConversionRate) }}</div>
          <div class="text-sm text-secondary">{{ dashboardData.appointmentsRenewed }} of {{ dashboardData.appointmentsOccurred }}</div>
        </div>
      </Card>

      <Card
        class="sales-dashboard__value"
        :is-loading="fetchDataRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="sales-card"
      >
        <div class="sales-card__header">
          <div>
            <div class="text-lg font-semibold">Value renewed</div>
            <div class="text-sm text-secondary">Revenue influenced</div>
          </div>
          <Avatar
            icon="sterling-sign"
            color="warning"
            size="md"
            no-border
          />
        </div>
        <div class="mt-8 text-4xl font-semibold">
          {{ formatNumber(dashboardData.valueRenewed, 'currency', 0) }}
        </div>
        <div class="mt-3 flex gap-3">
          <div class="sales-soft-stat">
            <span>{{ dashboardData.appointmentsRenewed }}</span>
            <small>renewals</small>
          </div>
          <div class="sales-soft-stat">
            <span>{{ dashboardData.appointmentsOccurred }}</span>
            <small>appointments</small>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import dayjs from 'dayjs'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { getDictionaryItem } from '@/dictionary'

import Card from '@/components/card/Card.vue'
import { formatNumber } from '@/utilities/number'

import Avatar from '@/components/avatar/Avatar.vue'

const authStore = useAuthStore()
const userSelected = ref(authStore.details.id)

const defaultDashboardData = {
  todaysAppointments: [],
  appointmentsOccurred: 0,
  appointmentsRenewed: 0,
  valueRenewed: 0,
  currentConversionRate: 0
}

const dashboardData = ref(defaultDashboardData)

const fetchDataRequest = useApiRequest()

const salesExecutiveName = computed(() => authStore.details.fullName ?? `${authStore.details.forename} ${authStore.details.surname}`)

const salesExecutiveFirstName = computed(() => salesExecutiveName.value.split(' ')[0] ?? 'there')

const greeting = computed(() => {
  const hour = dayjs().hour()

  if (hour < 12) {
    return 'Good morning'
  }

  if (hour < 18) {
    return 'Good afternoon'
  }

  return 'Good evening'
})

const performanceMetrics = computed(() => [
  {
    label: 'Appointments',
    value: dashboardData.value.appointmentsOccurred,
    icon: 'calendar',
    color: 'brand',
    severity: 'brand',
    hint: null
  },
  {
    label: 'Renewed',
    value: dashboardData.value.appointmentsRenewed,
    icon: 'check',
    color: 'success',
    severity: 'success',
    hint: formatPercent(dashboardData.value.currentConversionRate)
  },
  {
    label: 'Value',
    value: formatNumber(dashboardData.value.valueRenewed, 'currency', 0),
    icon: 'car',
    color: 'warning',
    severity: 'warning',
    hint: null
  }
])

const performanceSummary = computed(
  () =>
    `${dashboardData.value.appointmentsRenewed} renewals from ${dashboardData.value.appointmentsOccurred} appointments in the last 30 days.`
)

const appointmentSubtitle = computed(() =>
  dashboardData.value.todaysAppointments.length
    ? `${dashboardData.value.todaysAppointments.length} booked for today`
    : 'No appointments booked for today'
)

function getDashboardData() {
  if (!userSelected.value) {
    return
  }

  // Reset the dashboard data
  dashboardData.value = defaultDashboardData

  fetchDataRequest
    .send({
      endpoint: '/v1/dashboards/sales-executive',
      method: 'get',
      params: {
        userId: userSelected.value,
        start: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD')
      }
    })
    .then(response => {
      dashboardData.value = response.data
    })
}
getDashboardData()

function formatPercent(value) {
  return `${formatNumber((value ?? 0) * 100, 'normal', 1)}%`
}

</script>

<style scoped>
.sales-dashboard {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-height: calc(100vh - var(--s-8));
}

.sales-dashboard__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4);
}

.sales-dashboard__period {
  border-radius: var(--rounded-full);
  padding: var(--s-2) var(--s-4);
  background: rgba(255, 255, 255, 0.68);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  box-shadow: 0 12px 36px rgba(16, 24, 40, 0.08);
  backdrop-filter: blur(14px);
}

.sales-dashboard__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--s-4);
}

.sales-dashboard__profile {
  grid-column: span 2;
}

.sales-dashboard__appointments {
  grid-column: span 2;
  grid-row: span 2;
}

.sales-dashboard__metrics {
  grid-column: span 2;
}

.sales-dashboard :deep(.card) {
  border-radius: 2rem;
}

:deep(.sales-profile-card),
:deep(.sales-card) {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.07);
  backdrop-filter: blur(18px);
}

:deep(.sales-profile-card) {
  min-height: 20rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: var(--s-5);
  color: white;
  background:
    linear-gradient(180deg, rgba(30, 41, 59, 0.04), rgba(30, 41, 59, 0.58)),
    radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.26), transparent 11rem),
    linear-gradient(135deg, #93c5fd, #c4b5fd 54%, #fdba74);
}

.sales-profile-card__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--s-4);
}

:deep(.sales-card) {
  height: 100%;
  padding: var(--s-5);
}

.sales-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-3);
  margin-bottom: var(--s-4);
}

.sales-count-pill,
.sales-rate-pill,
.sales-time-pill {
  border-radius: var(--rounded-full);
  padding: var(--s-1) var(--s-3);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 600;
}

.sales-appointment-list {
  display: flex;
  flex-direction: column;
  gap: var(--s-3);
}

.sales-appointment-row {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  border-radius: 1.25rem;
  padding: var(--s-3);
  background: rgba(255, 255, 255, 0.62);
  color: inherit;
}

.sales-empty-state {
  border: 1px dashed var(--border-secondary);
  border-radius: 1.5rem;
  padding: var(--s-6);
  text-align: center;
}

.sales-metric-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--s-3);
}

.sales-metric-tile,
.sales-soft-stat {
  border-radius: 1.25rem;
  padding: var(--s-4);
  background: rgba(255, 255, 255, 0.62);
}

:deep(.sales-card--conversion) {
  background:
    radial-gradient(circle at 80% 14%, rgba(147, 197, 253, 0.2), transparent 12rem),
    rgba(255, 255, 255, 0.76);
}

.sales-conversion-visual {
  display: flex;
  min-height: 11rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.62);
  text-align: center;
}

.sales-conversion-visual__rate {
  font-size: clamp(3rem, 7vw, 5.5rem);
  font-weight: 700;
  line-height: 1;
}

.sales-soft-stat {
  flex: 1;
}

.sales-soft-stat span,
.sales-soft-stat small {
  display: block;
}

.sales-soft-stat span {
  font-size: 1.35rem;
  font-weight: 600;
}

.sales-soft-stat small {
  color: var(--text-secondary);
}

.dark .sales-dashboard__period,
.dark :deep(.sales-card) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

.dark :deep(.sales-profile-card) {
  border-color: rgba(255, 255, 255, 0.1);
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0.72)),
    radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.12), transparent 11rem),
    linear-gradient(135deg, #1e3a8a, #4c1d95 58%, #7c2d12);
}

.dark .sales-appointment-row,
.dark .sales-metric-tile,
.dark .sales-soft-stat,
.dark .sales-conversion-visual {
  background: rgba(255, 255, 255, 0.06);
}

.dark :deep(.sales-card--conversion) {
  background:
    radial-gradient(circle at 80% 14%, rgba(147, 197, 253, 0.12), transparent 12rem),
    rgba(17, 24, 39, 0.78);
}

@media (max-width: 1280px) {
  .sales-dashboard__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sales-dashboard__profile,
  .sales-dashboard__appointments,
  .sales-dashboard__metrics {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .sales-dashboard__topbar {
    flex-direction: column;
    align-items: stretch;
    overflow-x: auto;
  }

  .sales-dashboard__grid,
  .sales-metric-strip {
    grid-template-columns: 1fr;
  }

  .sales-dashboard__profile,
  .sales-dashboard__appointments,
  .sales-dashboard__metrics {
    grid-column: span 1;
  }
}
</style>
